import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";

const cache = new Map();   // file_id → object URL | null (null = no preview exists)

/**
 * FileThumb — a small preview so nobody downloads a file just to see what it is. Fetched as a
 * blob because the endpoint is authenticated, cached per file id for the session, and silently
 * absent for anything that can't be rendered.
 */
export const FileThumb = ({ fileId, alt = "", className = "", size = 44, fallback = null, hasThumb }) => {
  const [url, setUrl] = useState(() => cache.get(fileId) ?? undefined);
  const alive = useRef(true);

  useEffect(() => {
    alive.current = true;
    if (hasThumb === false) { setUrl(null); return () => { alive.current = false; }; }
    if (cache.has(fileId)) { setUrl(cache.get(fileId)); return () => { alive.current = false; }; }
    api.get(`/hub/files/${fileId}/thumb`, { responseType: "blob" })
      .then((r) => {
        const u = URL.createObjectURL(r.data);
        cache.set(fileId, u);
        if (alive.current) setUrl(u);
      })
      .catch(() => { cache.set(fileId, null); if (alive.current) setUrl(null); });
    return () => { alive.current = false; };
  }, [fileId, hasThumb]);

  if (!url) return fallback;
  return (
    <img src={url} alt={alt} loading="lazy"
         style={{ width: size, height: size }}
         className={`rounded-lg object-cover object-top border border-slate-200 bg-white shrink-0 ${className}`}
         data-testid={`file-thumb-${fileId}`} />
  );
};

/** FilePreview — full-width preview strip, for cards with room to show one properly. */
export const FilePreview = ({ fileId, alt = "" }) => {
  const [url, setUrl] = useState(() => cache.get(fileId) ?? undefined);

  useEffect(() => {
    let alive = true;
    if (cache.has(fileId)) { setUrl(cache.get(fileId)); return () => { alive = false; }; }
    api.get(`/hub/files/${fileId}/thumb`, { responseType: "blob" })
      .then((r) => { const u = URL.createObjectURL(r.data); cache.set(fileId, u); if (alive) setUrl(u); })
      .catch(() => { cache.set(fileId, null); if (alive) setUrl(null); });
    return () => { alive = false; };
  }, [fileId]);

  if (!url) return null;
  return (
    <div className="mt-2 rounded-lg overflow-hidden border border-slate-200 bg-white max-h-40 flex items-start justify-center">
      <img src={url} alt={alt} loading="lazy" className="w-full object-cover object-top"
           data-testid={`file-preview-${fileId}`} />
    </div>
  );
};
